/**
 * Surface → route-group → home mapping — the SINGLE source of truth for the staff/applicant
 * split. Every route guard and post-login redirect derives its target from here; nothing
 * inlines an `administrator || coordinator` check. Surface names come from the verified
 * bundle→surface table in the auth contract (auth-v1.md "Safe navigation context").
 */

/** The product surfaces a bundle can grant. */
export const SURFACES = [
  "team", // President and Administrator (the rebuilt model)
  "applicant_workspace", // applicant
  "intake_operations", // program_coordinator
  "round_configuration", // administrator
  "statistics", // observer
] as const;

export type Surface = (typeof SURFACES)[number];

/** The top-level route trees the app is split into. */
export type RouteGroup = "staff" | "applicant";

/**
 * Which tree each surface belongs to.
 *
 * `null` means the surface owns a bundle but has NO tree yet. `statistics` (observer) sits there
 * still — that surface has no built screen at all, so grouping it under `staff` would let an
 * observer walk into administrator screens with nothing of their own to land on. It lands on the
 * no-access terminal until its own screens exist.
 *
 * `intake_operations` (coordinator) joined the `staff` tree 2026-09-13 (DZCH-87's own follow-up):
 * every operations screen (queue/applications/waiting/dossier detail) already lives under
 * `/staff/*`, Figma draws no separate coordinator UI, and the backend already treats
 * `program_coordinator`/`administrator` as permission-equal on those screens (confirmed against
 * `_require_round`/`CAPABILITIES` in `operations/services.py`). Sharing the GROUP does not share
 * every SCREEN — `requireAnySurface` (`routes/-guards.ts`) widens only the routes a coordinator
 * should reach; round create/setup, the question bank, the competency library and Équipe stay
 * gated to `round_configuration` alone via the plain `requireSurface` guard they already used.
 */
const SURFACE_GROUP: Record<Surface, RouteGroup | null> = {
  team: null, // its own frame (`/team`), not the old /staff tree
  applicant_workspace: "applicant",
  intake_operations: "staff",
  round_configuration: "staff",
  statistics: null,
};

/**
 * Terminal landing for an authenticated user whose surfaces grant no built screen yet.
 *
 * TOP-LEVEL, not inside a tree — that is load-bearing. It is the destination for surfaces that
 * belong to NO group, so parking it under `/staff` would make it unreachable by the very users
 * it exists for: the group guard would bounce them to their landing, which is this page, which
 * is behind the guard that just bounced them. Its own route asserts authentication only.
 */
export const NO_ACCESS_PATH = "/no-access" as const;

/**
 * Landing route per SURFACE — the single source of truth for "where does this user start".
 * Guards and the post-login redirect both resolve through it, so gating a surface is a one-line
 * change here rather than an inline bundle check somewhere in the route tree.
 *
 * `statistics` (observer) still points at the no-access terminal — no screen exists for it yet.
 * The values are literal route paths so `redirect({ to })` stays type-safe end to end.
 *
 * The administrator lands on `/staff/rounds`. It was `/staff/dashboard` — deliberately empty while
 * the admin side was rebuilt after the product pivot — but the rounds list is the built screen and
 * the one an administrator actually starts from, so landing on an empty page was a detour on every
 * sign-in. The dashboard route stays reachable by URL; it is simply not where anyone begins.
 *
 * The coordinator ALSO lands on `/staff/rounds` (2026-09-13) — not a round-scoped queue directly,
 * because nothing establishes "which round" at login time (US-E7-01's own queue is per-round, and
 * this deployment's own working assumption is one open round at a time, but that is a product
 * decision — DZCH-142 — not a routing one to bake in here). `RoundCard`/`RoundsScreen` render the
 * coordinator's OWN narrower set of actions on that same list (no create/edit/extend — only "Voir
 * les dossiers"), so sharing the landing route does not mean sharing every action on it.
 */
const SURFACE_HOME = {
  team: "/team",
  applicant_workspace: "/applicant/home",
  intake_operations: "/staff/rounds",
  round_configuration: "/staff/rounds",
  statistics: NO_ACCESS_PATH,
} as const satisfies Record<Surface, string>;

export type SurfaceHome = (typeof SURFACE_HOME)[Surface];

/** The unauthenticated entry. Login lives at the app root this pass (no `/login` detour). */
export const LOGIN_PATH = "/" as const;

function isSurface(value: string): value is Surface {
  return (SURFACES as readonly string[]).includes(value);
}

/** The group a single surface belongs to, or `null` when it isn't a known surface. */
export function groupForSurface(surface: string): RouteGroup | null {
  return isSurface(surface) ? SURFACE_GROUP[surface] : null;
}

/**
 * Landing route for a `default_surface`. An unknown/empty surface falls back to the applicant
 * home (least-privileged landing) rather than throwing — a stray surface must never break the
 * login redirect.
 */
export function surfaceToHome(defaultSurface: string): SurfaceHome {
  return isSurface(defaultSurface)
    ? SURFACE_HOME[defaultSurface]
    : SURFACE_HOME.applicant_workspace;
}

/**
 * Whether any of the caller's `available_surfaces` belongs to `group` — the check every route
 * guard uses to allow or deny a group. A user with no surface in the group is rejected.
 */
export function canAccessGroup(
  availableSurfaces: readonly string[],
  group: RouteGroup
): boolean {
  return availableSurfaces.some(
    (surface) => groupForSurface(surface) === group
  );
}

/**
 * Whether the caller holds one specific surface — the PER-SCREEN gate, one level finer than
 * {@link canAccessGroup}. The group guard only proves "is staff"; a screen that belongs to a
 * single bundle (e.g. round configuration → administrator) must also assert its own surface,
 * otherwise every staff bundle walks into it.
 */
export function hasSurface(
  availableSurfaces: readonly string[],
  surface: Surface
): boolean {
  return availableSurfaces.includes(surface);
}
